import { forwardRef } from 'react';
import { Cross2Icon } from '@radix-ui/react-icons';

import { comboboxMultiValueStyles, comboboxBadgeStyles } from './combobox.styles';
import type { ComboboxMultiValueProps } from './combobox.types';

export const ComboboxMultiValue = forwardRef(
  <TValue extends string | number = string>(
    {
      options,
      onRemove,
      size = 'md',
      colorPalette = 'blue',
      maxVisible = 3,
      showCount = true,
      disabled,
      className,
    }: ComboboxMultiValueProps<TValue>,
    ref: React.Ref<HTMLDivElement>
  ) => {
    const visibleOptions = options.slice(0, maxVisible);
    const remainingCount = options.length - maxVisible;

    if (options.length === 0) {
      return null;
    }

    return (
      <div ref={ref} className={comboboxMultiValueStyles({ size, className })}>
        {visibleOptions.map((option) => (
          <span
            key={String(option.value)}
            className={comboboxBadgeStyles({ colorPalette, disabled })}
            data-disabled={disabled}
          >
            <span className="combobox-badge-label truncate max-w-[120px]">{option.label}</span>
            {!disabled && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onRemove(option.value);
                }}
                className="combobox-badge-remove p-0.5 rounded hover:bg-white/20 transition-colors"
                aria-label={`Remove ${option.label}`}
                tabIndex={-1}
              >
                <Cross2Icon className="h-3 w-3" />
              </button>
            )}
          </span>
        ))}

        {showCount && remainingCount > 0 && (
          <span className="combobox-remaining-count text-xs text-gray-600 font-medium px-2 py-0.5">
            +{remainingCount}
          </span>
        )}

        {showCount && options.length > maxVisible && (
          <span className="combobox-total-count text-xs text-gray-500 ml-1">
            ({options.length} selected)
          </span>
        )}
      </div>
    );
  }
) as (<TValue extends string | number = string>(
  props: ComboboxMultiValueProps<TValue> & { ref?: React.Ref<HTMLDivElement> }
) => React.ReactElement) & { displayName?: string };

ComboboxMultiValue.displayName = 'ComboboxMultiValue';
